import * as React from "react";
import { useState, useEffect, useContext, useMemo } from "react";
import { useHistory, useParams, Link } from "react-router-dom";
import * as moment from "moment";
import { NotificationAlertContext } from "contexts/notificationAlertContext";
import _ from "lodash";

// react-bootstrap components
import {
  Button,
  Card,
  Collapse,
  Table,
  Form,
  Container,
  Row,
  Col,
  Spinner,
  Dropdown,
  DropdownButton,
} from "react-bootstrap";
import Select from "react-select";
import CustomReactDatetime from "components/Custom/CustomReactDatetime.js";
import CustomMultiDateSelect from "components/Custom/CustomMultiDateSelect";
import CustomTooltip from "components/Custom/CustomTooltip";
import { MultiValueWithCustomTooltip, customEllipsisMultiSelectStyles } from "components/Custom/CustomMultiSelect.js";
import CustomStatus from "components/Custom/CustomStatus";
import CloudwatchMetric from "views/Components/CloudwatchMetric.js";
import {
  environmentDetailsExternalSteps,
  channelActionModalSteps,
} from "views/Clients/Environments/EnvironmentSteps";
import { DateObject } from "react-multi-date-picker";
import ReactTable from "components/ReactTable/ReactTableWithDynamicPagination.js";
import { convertDateListToDateRangeString } from "utilities/dateUtilities";
import { copyToClipboard } from "utilities/commonUtilities";
import { useTour } from "@reactour/tour";

import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ListGroupItem,
  ListGroup,
} from "reactstrap";

import {
  getClientEnvironment,
  getEnvironmentResourceDetails,
  addMCUser,
  postChannelActionRequest,
  refreshChannelTokens,
  updateClientEnvironmentName,
  unregisterEnvironment,
  updateFavoriteChannels,
} from "graphql/queries.js";
import SweetAlert from "react-bootstrap-sweetalert";

import { API, graphqlOperation } from "aws-amplify";
import { isStatusInAccessibleState } from "utilities/environmentUtilities";
import { AuthContext } from "contexts/authContext";
import { getList } from "graphql/queries";

function Environment() {
    // Internal styles
  const styles = {
      infoIcon: {
          fontSize: "13px",
          cursor: "pointer"
      },
      actionButtonsRow: {
          marginTop: "15px"
      }
  };

  // Contexts
  const notify = useContext(NotificationAlertContext);
  const currentUser = useContext(AuthContext);
  const history = useHistory();
  const { clientId, clientEnvironmentId } = useParams();

  const groupName = currentUser.groupName;
  const isInternal = groupName === "internal";

  // States
  const [clientEnvironmentData, setClientEnvironmentData] = useState({
    client_name: "",
    client_environment_name: "",
    channels: [],
  });
  const [isInternalSpokeType, setIsInternalSpokeType] = useState(false);
  const [showFirstLoader, setShowFirstLoader] = useState(false);
  const [showLoader, setShowLoader] = useState(true);
  const [mcUserData, setMCUserData] = useState({});

  const [jobRanAt, setJobRanAt] = useState(null);
  const [startDate, setStartDate] = useState(moment().startOf("day"));
  const [endDate, setEndDate] = useState(moment().endOf("day"));

  const [tempStartDate, setTempStartDate] = useState(moment().startOf("day"));
  const [tempEndDate, setTempEndDate] = useState(moment().endOf("day"));
  const [isCustomDate, setCustomDate] = useState(false);
  const [metricDateList, setMetricDateList] = useState([new DateObject()]);
  const [resourceDetailsModalOpen, setResourceDetailsModalOpen] =
    useState(false);
  const [mcUserModalOpen, setMCUserModalOpen] = useState(false);
  const [channelActionModalOpen, setChannelActionModalOpen] = useState(false);

  const [deployStatus, setDeployStatus] = useState(null);
  const [resourceDetails, setResourceDetails] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [metrics, setMetrics] = useState([]);
  const metricsList = [
    { label: "ECS - CPU Utilization", value: "ECS_CPU_UTILIZATION" },
    { label: "ECS - Memory Utilization", value: "ECS_MEM_UTILIZATION" },
    { label: "ECS - Network TX", value: "ECS_NETWORK_TX" },
    { label: "RDS - CPU Utilization", value: "RDS_CPU_UTILIZATION" },
    { label: "RDS - Freeable Memory", value: "RDS_FREE_MEMORY" },
    { label: "RDS - Free Storage Space", value: "RDS_FREE_STORAGE" },
    { label: "RDS - Read IOPS", value: "RDS_READ_IOPS" },
    { label: "RDS - Write IOPS", value: "RDS_WRITE_IOPS" },
    { label: "RDS - Swap Usage", value: "RDS_SWAP_USAGE" },
    { label: "RDS - Database Connections", value: "RDS_DB_CONNECTIONS" },
    { label: "S3 - Bytes Uploaded", value: "S3_ARCHIVE_BYTES_UPLOADED" },
    { label: "S3 - Bytes Downloaded", value: "S3_ARCHIVE_BYTES_DOWNLOADED" },
    { label: "S3 - Get Requests", value: "S3_ARCHIVE_GET_REQUESTS" },
    { label: "S3 - Put Requests", value: "S3_ARCHIVE_PUT_REQUESTS" },
    { label: "S3 - 4xx Errors", value: "S3_ARCHIVE_4XX_ERRORS" },
    { label: "S3 - 5xx Errors", value: "S3_ARCHIVE_5XX_ERRORS" },
  ];
  const [selectedMetric, setSelectedMetric] = useState(metricsList[0].value);
  const authTypeMap = {
    BASIC: "BASIC",
    API_KEY: "API KEY",
    MC_AUTH: "MC AUTH",
  };

  const channelActionOptions = [
    { key: "none", value: "No Action" },
    { key: "suppress", value: "Block Email Alerts" },
    { key: "unsuppress", value: "Allow Email Alerts" },
    { key: "db", value: "Delete Database Entries" },
    { key: "resource", value: "Delete Cloud Infrastructure and MC Channels" },
    {
      key: "all",
      value: "Delete Cloud Infrastructure, MC Channels and Database Entries",
    },
  ];

  const channelFilterOptions = [
    { value: "active", label: "Active" },
    { value: "favorites", label: "Favorites" },
    { value: "entitled", label: "Entitled" },
    { value: "disabled", label: "Disabled" },
  ];

  const [multiSelected, setMultiSelected] = useState([]);
  const [isCodebuildAllowed, setIsCodebuildAllowed] = useState(false);

  const [alert, setAlert] = useState(null);
  const [nameEdit, setNameEdit] = useState(false);
  const [clientEnvironmentNameValue, setClientEnvironmentNameValue] =
    useState("");
  const [supportTierBackup, setSupportTierBackup] = useState({});

  const [actionOptionsList, setActionOptionsList] = useState(
    isInternal ? channelActionOptions : channelActionOptions.slice(0, -3)
  );
  const channelActionAllOptions = channelActionOptions;
  const channelActionBasicOptions = channelActionOptions.slice(0, -3);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [selectedChannelFilters, setSelectedChannelFilters] = useState([{ value: "active", label: "Active" }, { value: "entitled", label: "Entitled" }]);
  const [channelOptions, setChannelOptions] = useState([]);
  const [favoriteChannels, setFavoriteChannels] = useState(new Set());

  // Metrics Cards State
  const [metricsCards, setMetricsCards] = useState([
    {
      id: 1,
      title: "",
      isExpanded: false,
      startDate: moment().startOf("day"),
      endDate: moment().endOf("day"),
      tempStartDate: moment().startOf("day"),
      tempEndDate: moment().endOf("day"),
      isCustomDate: false,
      metricDateList: [new DateObject()],
      selectedChannels: [],
      selectedChannelFilters: [{ value: "active", label: "Active" }, { value: "entitled", label: "Entitled" }],
      channelData: [], // Card-specific channel data
      totalCount: 0,   // Card-specific total count
      isLoading: false, // Card-specific loading state
    }
  ]);
  const [expandedCards, setExpandedCards] = useState(new Set([1]));

  // Metrics Cards Management Functions
  const addNewMetricsCard = (insertAfterCardId = null) => {
    const newId = Math.max(...metricsCards.map(card => card.id)) + 1;
    const newCard = {
      id: newId,
      title: `${newId}`,
      isExpanded: false,
      startDate: moment().startOf("day"),
      endDate: moment().endOf("day"),
      tempStartDate: moment().startOf("day"),
      tempEndDate: moment().endOf("day"),
      isCustomDate: false,
      metricDateList: [new DateObject()],
      selectedChannels: [],
      selectedChannelFilters: [],
      channelData: [], // Card-specific channel data
      totalCount: 0,   // Card-specific total count
      isLoading: false, // Card-specific loading state
    };

    if (insertAfterCardId) {
      const insertIndex = metricsCards.findIndex(card => card.id === insertAfterCardId);
      setMetricsCards(prev => {
        const newCards = [...prev];
        newCards.splice(insertIndex, 0, newCard); // Insert above (before) the current card
        return newCards;
      });
    } else {
      setMetricsCards(prev => [...prev, newCard]);
    }

    setExpandedCards(prev => new Set([...Array.from(prev), newId]));

    notify("success", `Metrics card added successfully!`);
  };

  const removeMetricsCard = (cardId) => {
    if (metricsCards.length > 1) {
      // Find the card title before removing it
      const cardToRemove = metricsCards.find(card => card.id === cardId);
      const cardTitle = cardToRemove ? cardToRemove.title : `Metrics ${cardId}`;

      setMetricsCards(prev => prev.filter(card => card.id !== cardId));
      setExpandedCards(prev => {
        const newSet = new Set(prev);
        newSet.delete(cardId);
        return newSet;
      });

      // Show success toast notification
      notify("success", `Metrics card removed successfully!`);
    } else {
      // Show warning if trying to remove the last card
      notify("warning", "Cannot remove the last metrics card. At least one card must remain.");
    }
  };

  const toggleCardExpansion = (cardId) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cardId)) {
        newSet.delete(cardId);
      } else {
        newSet.add(cardId);
      }
      return newSet;
    });
  };

  const updateCardState = (cardId, updates) => {
    setMetricsCards(prev => prev.map(card =>
      card.id === cardId ? { ...card, ...updates } : card
    ));
  };

  const toggleFavoriteChannel = async (channelId, cardId) => {
    const isFavorite = favoriteChannels.has(channelId);
    const channel = clientEnvironmentData.channels?.find(
      (ch) => ch.channel_id === channelId
    );
    const channelName = channel?.channel_name || "this channel";

    try {
      setAlert(null);
      if (cardId) {
        updateCardState(cardId, { isLoading: true });
      } else {
        setShowLoader(true);
      }
      // Update local state first for immediate UI feedback
      let isAddFavorite = false;
      const newFavorites = new Set(favoriteChannels);
      if (newFavorites.has(channelId)) {
        newFavorites.delete(channelId);
        isAddFavorite = false;
      } else {
        newFavorites.add(channelId);
        isAddFavorite = true;
      }
      const response = await API.graphql(
        graphqlOperation(updateFavoriteChannels, {
          client_id: clientId,
          client_environment_id: clientEnvironmentId,
          favorite_channel_id: channelId,
          is_add_favorite: isAddFavorite,
        }),
      );

      if (response.data?.updateFavoriteChannels?.success) {
        notify(
          "success",
          `Channel ${isFavorite ? "removed from" : "added to"} favorites!`,
        );
        const filterObj = {
          offSet: 0,
          sortFilter: columnMapping[sortValue],
          sortType: ascOrDescValue,
          limit: currentLimit,
        };
        await fetchClientEnvironment(filterObj, false, cardId);
      } else {
        console.error(response.data?.updateFavoriteChannels?.message);
        notify("danger", "Failed to update favorite channels");
        if (cardId) {
          updateCardState(cardId, { isLoading: false });
        } else {
          setShowLoader(false);
        }
      }
    } catch (error) {
      console.error("Error updating favorite channels:", error);
      notify("danger", "Error updating favorite channels");
      if (cardId) {
        updateCardState(cardId, { isLoading: false });
      } else {
        setShowLoader(false);
      }
    }
  };


  // Tour Guide
  const { setIsOpen, setCurrentStep, setSteps } = useTour();

  const [tourGuideSteps, setTourGuideSteps] = useState(
    environmentDetailsExternalSteps
  );

  const inputClass = {
    all: "unset",
    backgroundColor: "transparent",
    border: "1px solid #30363D",
    borderRadius: "3px",
    padding: "3px",
    minWidth: `${clientEnvironmentNameValue?.length}ch`,
  };
  // Pagination Starts
  const columnMapping = {
    Status: "channel_status",
    "Support Tier": "support_tier",
    "Channel Name": "channel_name",
    Group: "channel_group_name",
    "Blocked Email Alerts": "suppress_support_event",
    Received: "received_messages",
    Queued: "queued_messages",
    Filtered: "filtered_messages",
    Errored: "errored_messages",
    "Sent/Transformed": "sent_messages",
    "Avg Process Time (sec)": "avg_process_time",
    "Auth Type": "mc_auth_type",
    "Last Reviewed": "last_reviewed_by_support",
  };

  const headers = [
    { key: "channel_status", label: "Channel Status" },
    { key: "support_tier", label: "Support Tier" },
    { key: "channel_name", label: "Channel Name" },
    { key: "suppress_support_event", label: "Blocked Email Alerts" },
    { key: "received_messages", label: "Received Messages" },
    { key: "queued_messages", label: "Queued Messages" },
    { key: "filtered_messages", label: "Filtered Messages" },
    { key: "errored_messages", label: "Errored Messages" },
    { key: "sent_messages", label: "Sent Messages" },
    { key: "avg_process_time", label: "Avg Process Time" },
    { key: "mc_auth_type", label: "Mc Auth Type" },
    { key: "last_reviewed_by_support", label: "Last Reviewed" },
  ];
  const [totalCount, setTotalCount] = useState(0);
  const numberOfRowsData = [25, 50, 100, 1000];

  const [currentLimit, setCurrentLimit] = useState(numberOfRowsData[3]);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [ascOrDescValue, setAscOrDescValue] = useState("ASC");
  const [sortValue, setSortValue] = useState("");

  const setStateForPagnination = async (key, value) => {
    if (key === "currentLimit") {
      setCurrentLimit(value);
    } else if (key === "offset") {
      setCurrentOffset(value);
    } else if (key === "ascOrDesc") {
      setAscOrDescValue(value);
    } else {
      setSortValue(value);
    }
  };
  // Pagination Ends

  const fetchClientEnvironment = async (
    variables = {},
    isCSVDownload = false,
    cardId = null
  ) => {
    try {
      // Set loading state for specific card or global
      if (cardId) {
        updateCardState(cardId, { isLoading: true });
      } else {
        setShowLoader(true);
      }

      // Use card-specific states if cardId is provided, otherwise use legacy states
      let cardData = null;
      if (cardId) {
        cardData = metricsCards.find(card => card.id === cardId);
      }

      const useCustomDate = cardData ? cardData.isCustomDate : isCustomDate;
      const useDateList = cardData ? cardData.metricDateList : metricDateList;
      const useStartDate = cardData ? cardData.tempStartDate : tempStartDate;
      const useEndDate = cardData ? cardData.tempEndDate : tempEndDate;
      const useSelectedChannels = cardData ? cardData.selectedChannels : selectedChannels;
      const useSelectedChannelFilter = cardData ? cardData.selectedChannelFilters : selectedChannelFilters;

      const payload = {
        isCustomDate: useCustomDate,
        sortFilter: variables.sortFilter ? variables.sortFilter : "",
        client_id: clientId,
        client_environment_id: clientEnvironmentId,
        metric_date_range_list: !useCustomDate
          ? convertDateListToDateRangeString(useDateList)
          : JSON.stringify([
            { start_date: useStartDate, end_date: useEndDate },
          ]),
        channel_ids: useSelectedChannels.map((channel) => channel.value),
        is_active: useSelectedChannelFilter.some(u => u.value === "active"),
        is_favorite: useSelectedChannelFilter.some(u => u.value === "favorites"),
        is_entitled: useSelectedChannelFilter.some(u => u.value === "entitled"),
        is_inactive: useSelectedChannelFilter.some(u => u.value === "disabled"),
      };
      variables = { ...variables, ...payload };
      const response = await API.graphql(
        graphqlOperation(getClientEnvironment, variables)
      );
      if (response.data?.getClientEnvironment?.success) {
        const data = _.get(
          response,
          "data.getClientEnvironment.body.client_environment_data"
        );
        const totalChannelCount = _.get(
          response,
          "data.getClientEnvironment.body.total_channel_count"
        );
        const isInternalSpokeTypeTemp = data.spoke_type === 0;
        if (!isCSVDownload) {
          if (cardId) {
            // Update card-specific data
            updateCardState(cardId, {
              channelData: data.channels || [],
              totalCount: totalChannelCount,
              isLoading: false
            });
          } else {
            // Update global data (for backward compatibility)
            setTotalCount(totalChannelCount);
            setClientEnvironmentData(data);
            // Also update the first card with initial data
            updateCardState(1, {
              channelData: data.channels || [],
              totalCount: totalChannelCount,
              isLoading: false
            });
          }

          setClientEnvironmentNameValue(data?.client_environment_name);
          setIsInternalSpokeType(isInternalSpokeTypeTemp);
          if (isInternalSpokeTypeTemp) {
            setTourGuideSteps(environmentDetailsExternalSteps);
          }
          let last_calculated_values = [];
          const supportTierBackupCopy = { ...supportTierBackup };
          data.channels.forEach((channel) => {
            if (channel.last_calculated) {
              last_calculated_values.push(new Date(channel.last_calculated));
            }
            if (!isCustomDate) {
              supportTierBackupCopy[channel.channel_id] = channel.support_tier;
            }
          });
          setSupportTierBackup(supportTierBackupCopy);
          const max_last_calculated =
            last_calculated_values.length > 0
              ? new Date(Math.max.apply(null, last_calculated_values))
              : null;
          setJobRanAt(max_last_calculated);
          setDeployStatus(data.deploy_status);
          setIsCodebuildAllowed(isStatusInAccessibleState(data.deploy_status));
          setFavoriteChannels(new Set(data.channels.map(channel => {
            if (channel.is_favorite) return channel.channel_id
            return null
          }).filter(Boolean)));
        } else {
          const filter = {
            "Client Environment Id": payload.client_environment_id,
            "Metric Date List": payload.metric_date_list,
            "Start Date": payload.start_date,
            "End Date": payload.end_date,
          };
          const messageDataForCSV = _.get(
            response,
            "data.getClientEnvironment.body.client_environment_data.channels"
          );
          return { messageDataForCSV, filter };
        }
        if (isInternal && isInternalSpokeTypeTemp) {
          setActionOptionsList(channelActionAllOptions);
        } else if (
          isInternal &&
          isInternalSpokeTypeTemp
        ) {
          setActionOptionsList([
            channelActionOptions[0],
            channelActionOptions[3],
          ]);
        } else {
          setActionOptionsList(channelActionBasicOptions);
        }
      } else {
        console.error(response.data?.getClientEnvironment?.message);
      }
      return response.data?.getClientEnvironment?.success;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      if (cardId) {
        updateCardState(cardId, { isLoading: false });
      } else {
        setShowLoader(false);
      }
    }
  };

  const environmentResourceDetails = async () => {
    try {
      setShowLoader(true);
      const response = await API.graphql(
        graphqlOperation(getEnvironmentResourceDetails, {
          clientEnvironmentId: clientEnvironmentId,
        })
      );
      if (response.data?.getEnvironmentResourceDetails?.success) {
        const data = response.data.getEnvironmentResourceDetails.body;
        if (
          !data.db_secrets &&
          !data.mc_secrets &&
          !data.mc_authorized_data_sources &&
          !data.mc_nlb_dns_name
        ) {
          setResourceDetails(null);
          return;
        }
        setResourceDetails(data);
        setResourceDetailsModalOpen(true);
        notify("success");
      } else {
        console.error(response.data?.getEnvironmentResourceDetails?.message);
        notify("danger");
      }
    } catch (error) {
      console.error(error);
      notify("danger");
    } finally {
      setShowLoader(false);
    }
  };

  const changeClientEnvironmentName = async () => {
    try {
      setAlert(
        <SweetAlert
          showCancel
          title={"Are you sure?"}
          onConfirm={async () => {
            setShowLoader(true);
            const variables = {
              client_environment_id: clientEnvironmentId,
              client_environment_name: clientEnvironmentNameValue,
            };
            const response = await API.graphql(
              graphqlOperation(updateClientEnvironmentName, variables)
            );
            if (response.data?.updateClientEnvironmentName?.success) {
              notify(
                "success",
                response.data?.updateClientEnvironmentName?.body?.response
              );
              const filterObj = {
                offSet: 0,
                sortFilter: columnMapping[sortValue],
                sortType: ascOrDescValue,
                limit: currentLimit,
              };
              await fetchClientEnvironment(filterObj);
              setNameEdit(false);
              setAlert(null);
            } else {
              console.error(
                response.data?.updateClientEnvironmentName?.message
              );
            }
            return response.data?.updateClientEnvironmentName?.success;
          }}
          onCancel={() => setAlert(null)}
          confirmBtnBsStyle="primary"
          cancelBtnBsStyle="danger"
          confirmBtnText="Confirm"
          cancelBtnText="Cancel"
        >
          You want to update the client environment's name to "
          {clientEnvironmentNameValue}" ?
        </SweetAlert>
      );
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      setShowLoader(false);
    }
  };

  const createMCUser = async () => {
    try {
      notify("info", "MC User creation requested");
      const response = await API.graphql(
        graphqlOperation(addMCUser, {
          client_environment_id: clientEnvironmentId,
          user_id: currentUser.userId,
          user_email: currentUser.emailId,
        })
      );
      if (response.data?.addMCUser?.success) {
        const mc_user = JSON.parse(response.data.addMCUser.message);
        setMCUserData(mc_user);
        setMCUserModalOpen(true);
      } else {
        console.error(response.data?.addMCUser?.message);
        notify("danger", "Failed to create the MC User");
      }
    } catch (error) {
      console.error(error);
      notify("danger", "Failed to process MC User create request");
    }
  };

  const requestRefreshChannelTokens = async () => {
    try {
      notify("info", "Refresh channel tokens requested");
      const response = await API.graphql(
        graphqlOperation(refreshChannelTokens, {
          client_environment_id: clientEnvironmentId,
        })
      );
      if (response.data?.refreshChannelTokens?.success) {
        notify("success", "Refresh channel tokens requested successfully");
      } else {
        console.error(response.data?.refreshChannelTokens?.message);
        notify("danger", "Failed requesting channel token refresh");
      }
    } catch (error) {
      console.error(error);
      notify("danger", "Failed to process refresh channel token request");
    }
  };

  const [multipleExpandablePanels, setMultipleExpandablePanels] = useState([]);
  const toggleMultipleExpandablePanels = (event, value) => {
    event.preventDefault();
    if (multipleExpandablePanels.includes(value)) {
      setMultipleExpandablePanels(
        multipleExpandablePanels.filter((prop) => prop !== value)
      );
    } else {
      setMultipleExpandablePanels([...multipleExpandablePanels, value]);
    }
  };

  const UnregisterEnvironment = async () => {
    try {
      setAlert(
        <SweetAlert
          showCancel
          title={"Are you sure?"}
          onConfirm={async () => {
            notify("info");
            setAlert(null);
            const response = await API.graphql(
              graphqlOperation(unregisterEnvironment, {
                client_environment_id: clientEnvironmentId,
              })
            );
            if (response.data?.unregisterEnvironment?.success) {
              notify("success", "Environment Unregistered Successfully");
              history.goBack();
            } else {
              console.error(response.data?.unregisterEnvironment?.message);
              notify("danger");
            }
          }}
          onCancel={() => setAlert(null)}
          confirmBtnBsStyle="primary"
          cancelBtnBsStyle="danger"
          confirmBtnText="Confirm"
          cancelBtnText="Cancel"
        >
          You want to unregister the environment?
        </SweetAlert>
      );
    } catch (error) {
      console.error(error);
      notify("danger", "Failed to unregister environment");
    }
  };

  const handleChannelActionSubmit = async (
    client_id,
    client_environment_id,
    channel_list,
    user_id
  ) => {
    let payload = {};
    payload.client_environment_id = client_environment_id;
    payload.user_id = user_id;

    let suppress_list = [];
    let unsuppress_list = [];
    let delete_resource_list = [];
    let delete_db_list = [];

    for (
      let channelActionOptionIndex = 0;
      channelActionOptionIndex < multiSelected.length;
      channelActionOptionIndex++
    ) {
      if (multiSelected[channelActionOptionIndex].key == "suppress") {
        suppress_list.push(channel_list[channelActionOptionIndex].channel_id);
      } else if (multiSelected[channelActionOptionIndex].key == "unsuppress") {
        unsuppress_list.push(channel_list[channelActionOptionIndex].channel_id);
      } else if (multiSelected[channelActionOptionIndex].key == "resource") {
        delete_resource_list.push(
          channel_list[channelActionOptionIndex].channel_id
        );
      } else if (multiSelected[channelActionOptionIndex].key == "db") {
        delete_db_list.push(channel_list[channelActionOptionIndex].channel_id);
      } else if (multiSelected[channelActionOptionIndex].key == "all") {
        delete_resource_list.push(
          channel_list[channelActionOptionIndex].channel_id
        );
        delete_db_list.push(channel_list[channelActionOptionIndex].channel_id);
      }
    }

    payload.suppress_list = suppress_list;
    payload.unsuppress_list = unsuppress_list;
    payload.delete_resource_list = delete_resource_list;
    payload.delete_db_list = delete_db_list;

    try {
      if (
        suppress_list.length == 0 &&
        unsuppress_list.length == 0 &&
        delete_resource_list.length == 0 &&
        delete_db_list.length == 0
      ) {
        notify(
          "info",
          "Channel action request was not sent because no actions were changed."
        );
      } else {
        notify("info", "Channel action requested.");
        const response = await API.graphql(
          graphqlOperation(postChannelActionRequest, payload)
        );

        if (response.data?.postChannelActionRequest?.success) {
          notify("success", "Successfully sent channel action request.");
          history.push(`/admin/clients/${client_id}`);
        } else {
          notify("warning", "Failed to send channel action request.");
        }
      }
    } catch (e) {
      notify("warning", "Failed to get response after channel action request.");
    }
  };

  function handleMultiSelect(key, event, index) {
    let new_list = [];
    for (
      let select_index = 0;
      select_index < multiSelected.length;
      select_index++
    ) {
      if (select_index === index) {
        new_list.push({ key: key, value: event.target.text });
      } else {
        new_list.push(multiSelected[select_index]);
      }
    }
    setMultiSelected(new_list);
  }

  function resetSelect(num_select, defaultSelect) {
    let new_list = [];
    for (
      let num_select_index = 0;
      num_select_index < num_select;
      num_select_index++
    ) {
      new_list.push(actionOptionsList[defaultSelect]);
    }
    setMultiSelected(new_list);
  }

  function handleChannelActionAllSelect(key, num_select) {
    let selected_index = actionOptionsList.findIndex((x) => x.key === key);
    let new_list = [];
    for (
      let channel_index = 0;
      channel_index < num_select;
      channel_index++
    ) {
      if (
        clientEnvironmentData.channels[channel_index].channel_library_id ||
        selected_index < 3
      ) {
        new_list.push(actionOptionsList[selected_index]);
      } else {
        new_list.push(multiSelected[channel_index]);
      }
    }
    setMultiSelected(new_list);
  }

  // Fetch options for clients
  const fetchList = async () => {
    try {
      const response = await API.graphql(
        graphqlOperation(getList, {
          type: "CHANNELS_ALL",
          id: clientEnvironmentId,
          auth_client_id: currentUser.clientId,
        })
      );
      if (response.data?.getList?.success) {
        const options = response.data.getList
          ? response.data.getList.body
              .map((channel) => {
                const option = {
                  value: channel.id,
                  label: channel.name,
                };
                return option;
              })
              .sort((a, b) => a.label.localeCompare(b.label))
          : [];
        setChannelOptions(options);
      } else {
        console.error(response.data?.getList?.message);
        notify("danger", `Could not fetch channels list.`);
      }
      return response.data?.getList?.success;
    } catch (error) {
      console.error(error);
      notify("danger", `Could not fetch channels list.`);
      return false;
    }
  };

  useEffect(() => {
    async function fetchData() {
      setShowFirstLoader(true);
      const filterObj = {
        offSet: 0,
        sortFilter: columnMapping[sortValue],
        limit: currentLimit,
      };
      setCurrentOffset(0);
      const promiseAllResults = await Promise.all([
        fetchList(),
        fetchClientEnvironment(filterObj), // Load data for global and first card
      ]);
      setShowFirstLoader(false);
      const isSuccess = promiseAllResults.every((value) => value);
      if (!isSuccess) {
        notify("danger");
        history.goBack();
      }
    }
    fetchData();
  }, []);

  const mapSupportTier = (level) => {
    switch (level) {
      case 1:
        return "Low";
      case 2:
        return "Medium";
      case 3:
        return "Critical";
      default:
        return "N/A";
    }
  };

  const removeMetric = (index) =>
    setMetrics((oldMetrics) => {
      const oldMetricsCopy = [...oldMetrics];
      oldMetricsCopy.splice(index, 1);
      return oldMetricsCopy;
    });

  const metricCardsHTML = metrics.map(function (metric_type, index) {
    return (
      <Col lg="6" sm="12">
        <CloudwatchMetric
          key={index}
          client_environment_id={clientEnvironmentId}
          metric_type={String(metric_type)}
          index={index}
          removeMetric={removeMetric}
        />
      </Col>
    );
  });

  const metricsListHTML = metricsList.map(function (metric, index) {
    return (
      <ListGroupItem
        className={`list-group-item-action ${
          selectedMetric === metric.value ? "active" : ""
        }`}
        style={{
          cursor: "pointer",
        }}
        onClick={() => setSelectedMetric(metric.value)}
        tag="a"
        key={index}
      >
        {metric.label}
      </ListGroupItem>
    );
  });

  if (showFirstLoader || showLoader) return <Spinner animation="grow" />;

  return (
    <>
      {alert}
      <Container fluid>
        <Row>
          <Col>
            <h2>Environment Details</h2>
          </Col>
          <Col className="my-auto text-right">
            {isInternal && isInternalSpokeType && (
              <>
                <Button
                  variant="primary"
                  className="ml-4"
                  onClick={() => {
                    environmentResourceDetails();
                  }}
                >
                  Show Resource Details
                </Button>
                <Button
                  variant="primary"
                  className="ml-4"
                  onClick={() => {
                    createMCUser();
                  }}
                >
                  My MC User
                </Button>
              </>
            )}
            {isInternal && (
              <Button
                variant="primary"
                className="ml-4"
                onClick={() => setModalOpen(true)}
              >
                <i className="fas fa-plus"></i> Add Metric
              </Button>
            )}
            {currentUser.groupName == "external" && (
              <Button
                variant="secondary"
                className="btn-outline ml-4"
                onClick={() => {
                  setSteps(tourGuideSteps);
                  setCurrentStep(0);
                  setIsOpen(true);
                }}
              >
                <i className="fas fa-desktop"></i> View Our Tour Guide
              </Button>
            )}
          </Col>
        </Row>
        <Row>
          <Col md="12" sm="12" id="environment-details-header-1">
            <h5>Client Name: {clientEnvironmentData.client_name}</h5>
            <h5>
              Environment Name:&nbsp;
              {!nameEdit ? (
                <>
                  <span>{clientEnvironmentData?.client_environment_name}</span>
                  <Button
                    variant="primary"
                    className="btn-simple"
                    onClick={() => setNameEdit(true)}
                  >
                    <i className="fas fa-edit"></i>
                  </Button>
                </>
              ) : (
                <>
                  <Form.Control
                    type="text"
                    autoFocus
                    required
                    style={inputClass}
                    value={clientEnvironmentNameValue}
                    minLength="1"
                    maxLength="80"
                    onChange={(e) =>
                      setClientEnvironmentNameValue(e.target.value)
                    }
                    name="client_environment_name"
                  />
                  <span>
                    <Button
                      variant="success"
                      className="btn-simple"
                      onClick={() => {
                        if (clientEnvironmentNameValue.trim() === "") {
                          notify(
                            "warning",
                            "Client Environment Name cannot be empty"
                          );
                        } else {
                          changeClientEnvironmentName();
                        }
                      }}
                    >
                      <i className="fas fa-check"></i>
                    </Button>
                    <Button
                      variant="danger"
                      className="btn-simple"
                      onClick={() => {
                        setClientEnvironmentNameValue(
                          clientEnvironmentData?.client_environment_name
                        );
                        setNameEdit(false);
                      }}
                    >
                      <i className="fas fa-times"></i>
                    </Button>
                  </span>
                </>
              )}
            </h5>
          </Col>
          <Col md="12" sm="12" id="environment-details-header-2">
            <h5>Environment Id: {clientEnvironmentId}</h5>
            <h5>Deploy Status: {deployStatus}</h5>
            <h5>
              Environment Status:{" "}
              <CustomStatus
                value={clientEnvironmentData.client_environment_status}
              />
            </h5>
            <h5>Spoke Name: {clientEnvironmentData.spoke_name}</h5>
            <h5>
              Spoke Type: {isInternalSpokeType ? "Fully Managed" : "Remote"}
            </h5>
          </Col>
        </Row>
        <Row>
          <Col>
            <Button
              variant="primary"
              className="btn-outline"
              id="channel-action-button"
              disabled={clientEnvironmentData.channels?.length == 0}
              onClick={() => {
                resetSelect(clientEnvironmentData.channels?.length, 0);
                setChannelActionModalOpen(true);
              }}
            >
              Channel Action
            </Button>
            {isInternalSpokeType && (
              <Button
                variant="primary"
                className="btn-outline ml-4"
                id="refresh-channel-tokens-button"
                onClick={() => {
                  requestRefreshChannelTokens();
                }}
              >
                Refresh Channel Tokens
              </Button>
            )}
            {isInternal && isInternalSpokeType && (
              <Link
                className="btn btn-primary btn-outline ml-4"
                id="manage-firewall-button"
                to={{
                  pathname: `/admin/clients/${clientId}/environments/${clientEnvironmentId}/manage-firewall`,
                  state: {
                    clientEnvironmentName:
                      clientEnvironmentData.client_environment_name,
                    clientName: clientEnvironmentData.client_name,
                  },
                }}
              >
                Manage Firewall
              </Link>
            )}
            <Link
              id="mc-server-info-button"
              className="btn btn-primary btn-outline ml-4"
              to={{
                pathname: `/admin/mc-server-info`,
                state: {
                  environment_ids: [
                    {
                      label: clientEnvironmentData?.client_environment_name,
                      value: clientEnvironmentData?.client_environment_id,
                    },
                  ],
                  client_ids: [
                    {
                      label: clientEnvironmentData?.client_name,
                      value: clientEnvironmentData?.client_id,
                    },
                  ],
                },
              }}
            >
              MC Server Info
            </Link>
            {isInternal && !isInternalSpokeType && (
              <Button
                variant="danger"
                className="btn-outline ml-4"
                disabled={false}
                onClick={() => {
                  UnregisterEnvironment();
                }}
              >
                Unregister Environment
              </Button>
            )}
            {(!isInternalSpokeType || isInternal) && (
              <Link
                className="btn btn-primary btn-outline ml-4"
                id="environment-entitlement-button"
                to={{
                  pathname: `/admin/clients/${clientId}/environments/${clientEnvironmentId}/manage-entitlements`,
                }}
              >
                Environment Entitlements
              </Link>
            )}
            {isCodebuildAllowed && (
              <>
                <Link
                  className="btn btn-primary btn-outline ml-4"
                  id="update-environment-button"
                  to={{
                    pathname: `/admin/clients/${clientId}/environments/${clientEnvironmentId}/update-environment`,
                    state: {
                      isInternal: isInternal,
                      isInternalSpokeType: isInternalSpokeType,
                    },
                  }}
                >
                  Update Environment
                </Link>
                <Link
                  className="btn btn-primary btn-outline ml-4"
                  id="modify-channels-button"
                  to={{
                    pathname: `/admin/clients/${clientId}/environments/${clientEnvironmentId}/update-channels`,
                  }}
                >
                  Modify Channels
                </Link>
              </>
            )}
          </Col>
        </Row>
        <Row>{metricCardsHTML}</Row>


        {/* Metrics Cards with Accordion */}
        {metricsCards.map((card) => {
          const isExpanded = expandedCards.has(card.id);
          return (
            <Row key={card.id} className="mb-4">
              <Col>
                <Card>
                  <Card.Header
                    style={{
                      cursor: 'pointer'
                    }}
                    onClick={() => toggleCardExpansion(card.id)}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        <div>
                          <Card.Title className="mb-0 d-flex align-items-center" style={{ color: '#495057' }}>
                            <i
                              className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} mr-2`}
                              style={{
                                transition: 'transform 0.2s ease',
                                color: '#6c757d'
                              }}
                            ></i>
                          </Card.Title>
                          <Card.Subtitle className="mb-0 mt-1" style={{ color: '#6c757d', fontSize: '0.75rem' }}>
                            {jobRanAt
                              ? `Last calculated at ${jobRanAt.toLocaleString("en-US")}`
                              : `No data to show for selected date(s)`}
                          </Card.Subtitle>
                        </div>
                      </div>
                      <div className="d-flex align-items-center">
                        {metricsCards.length > 1 && (
                          <Button
                            variant="danger"
                            className="btn btn-danger btn-outline ml-4"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent accordion toggle
                              removeMetricsCard(card.id);
                            }}
                            title="Remove this metrics card"
                          >
                            <i className="fas fa-times mr-1"></i>
                            Remove Search Metrics
                          </Button>
                        )}
                        <Button
                          variant="primary"
                          className="ml-4"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent accordion toggle
                            addNewMetricsCard(card.id);
                          }}
                          title="Add new metrics card above this one"
                        >
                          <i className="fas fa-plus mr-1"></i>
                          Add Search Metrics
                        </Button>
                      </div>
                    </div>
                  </Card.Header>
                  <hr style={{ margin: 0, borderColor: '#dee2e6' }} />
                  <Collapse in={isExpanded}>
                    <div>
                      <Card.Body>
                        {/* Loading Indicator */}
                        {card.isLoading && (
                          <Row className="mb-3">
                            <Col className="text-center">
                              <Spinner animation="border" role="status" size="sm" className="mr-2" />
                              <span>Loading metrics data...</span>
                            </Col>
                          </Row>
                        )}

                        {/* Metrics Search Section */}
                        {!card.isLoading && (
                          <Row id={`channel-search-filter-${card.id}`} className="align-items-end mb-4">
                            <Col md="1" style={{ marginBottom: "8px" }}>
                              <Form.Check>
                                <Form.Check.Label>
                                  <Form.Check.Input
                                    type="checkbox"
                                    checked={card.isCustomDate}
                                    onChange={(event) => {
                                      updateCardState(card.id, { isCustomDate: event.target.checked });
                                    }}
                                  ></Form.Check.Input>
                                  <span className="form-check-sign"></span>
                                  Date Range
                                </Form.Check.Label>
                              </Form.Check>
                            </Col>
                            {card.isCustomDate ? (
                              <>
                                <Col md="2">
                                  <label>Select Start Date:</label>
                                  <CustomReactDatetime
                                    inputProps={{
                                      className: "form-control",
                                      placeholder: "Start Time",
                                    }}
                                    timeFormat="h:mm A"
                                    value={card.tempStartDate}
                                    onChange={(e) => updateCardState(card.id, { tempStartDate: e })}
                                  ></CustomReactDatetime>
                                </Col>
                                <Col md="2">
                                  <label>Select End Date:</label>
                                  <CustomReactDatetime
                                    inputProps={{
                                      className: "form-control",
                                      placeholder: "End Time",
                                    }}
                                    timeFormat="h:mm A"
                                    value={card.tempEndDate}
                                    onChange={(e) => updateCardState(card.id, { tempEndDate: e })}
                                  ></CustomReactDatetime>
                                </Col>
                              </>
                            ) : (
                              <>
                                <Col md="4">
                                  <label
                                    style={{
                                      paddingRight: "10px",
                                    }}
                                  >
                                    Select Date(s):
                                  </label>
                                  <CustomMultiDateSelect
                                    value={card.metricDateList}
                                    onChange={(e) => updateCardState(card.id, { metricDateList: e })}
                                  ></CustomMultiDateSelect>
                                </Col>
                              </>
                            )}
                            <Col md="3" sm="6">
                              <label>Channel Name Filter</label>
                              <Select
                                className="react-select primary"
                                classNamePrefix="react-select"
                                name={`channelSelect-${card.id}`}
                                closeMenuOnSelect={false}
                                isMulti
                                styles={customEllipsisMultiSelectStyles}
                                value={card.selectedChannels}
                                onChange={(clientEnv) => {
                                  updateCardState(card.id, { selectedChannels: clientEnv ? clientEnv : [] });
                                }}
                                options={channelOptions}
                                isClearable={true}
                                placeholder="None Selected"
                              />
                            </Col>
                            <Col md="3" sm="6">
                              <label>Channel Visibility Filter
                                <CustomTooltip id={`channelFilterTooltip-${card.id}`} value="These filters will be applied along with the 'Channel Name Filter' to narrow search results" placement="left">
                                  <i className="fa fa-info-circle" style={styles.infoIcon}></i>
                                </CustomTooltip>
                              </label>
                              <Select
                                className="react-select primary"
                                classNamePrefix="react-select"
                                name={`channelFilter-${card.id}`}
                                closeMenuOnSelect={false}
                                isMulti
                                styles={customEllipsisMultiSelectStyles}
                                value={card.selectedChannelFilters}
                                onChange={(filter) => {
                                  updateCardState(card.id, { selectedChannelFilters: filter ? filter : [] });
                                }}
                                options={channelFilterOptions}
                                isClearable={true}
                                placeholder="None Selected"
                              />
                            </Col>
                            <Col md="1">
                              <Button
                                variant="primary"
                                onClick={async () => {
                                  const errors = [];

                                  // Validate start date is not after end date
                                  if (card.tempStartDate.isAfter(card.tempEndDate)) {
                                    errors.push(
                                      "Start date must be lesser than end date"
                                    );
                                  }
                                  // Show all errors if any exist
                                  if (errors.length > 0) {
                                    errors.forEach((error) => notify("danger", error));
                                    return;
                                  }
                                  updateCardState(card.id, {
                                    startDate: card.tempStartDate,
                                    endDate: card.tempEndDate
                                  });

                                  const filterObj = {
                                    offSet: 0,
                                    sortFilter: "",
                                    sortType: ascOrDescValue,
                                    limit: currentLimit,
                                  };
                                  setCurrentOffset(0);
                                  const isSuccess = await fetchClientEnvironment(
                                    filterObj,
                                    false,
                                    card.id
                                  );
                                  if (isSuccess) notify("success");
                                  else notify("danger");
                                }}
                              >
                                Search
                              </Button>
                            </Col>
                          </Row>
                        )}

                        {/* Channel List Section */}
                        {!card.isLoading && (
                          <Row>
                            <Col id={`channel-data-grid-${card.id}`}>
                              <ReactTable
                                data={card.channelData?.map((channel, index) => {
                                  return {
                                    id: channel.channel_id,
                                    status: <CustomStatus value={channel.channel_status} />,
                                    name: channel.enabled ? (
                                      isInternalSpokeType ? (
                                        <Link
                                          to={{
                                            pathname: `/admin/clients/${clientId}/environments/${clientEnvironmentId}/channels-legacy/${channel.channel_id}`,
                                          }}
                                          key={index}
                                        >
                                          {channel.channel_name}
                                        </Link>
                                      ) : (
                                        <Link
                                          to={{
                                            pathname: `/admin/clients/${clientId}/environments/${clientEnvironmentId}/channels/${channel.channel_id}`,
                                          }}
                                          key={index}
                                        >
                                          {channel.channel_name}
                                        </Link>
                                      )
                                    ) : (
                                      channel.channel_name
                                    ),
                                    action: (
                                      <Link
                                        to={{
                                          pathname: isInternalSpokeType ? "/admin/find-or-reprocess" : "/admin/find-or-reprocess-manifest",
                                          state: {
                                            ...channel,
                                            ...clientEnvironmentData,
                                            channelName: channel.channel_name,
                                            channelId: channel.channel_id,
                                            clientId: clientEnvironmentData.client_id,
                                            clientName: clientEnvironmentData.client_name,
                                            clientEnvironmentId: clientEnvironmentId,
                                            clientEnvironmentName:
                                              clientEnvironmentData.client_environment_name,
                                            isCustomDate: isCustomDate,
                                            startDate: startDate.format(),
                                            endDate: endDate.format(),
                                            isEndDateChanged: true,
                                          },
                                        }}
                                      >
                                        Go to Messages
                                      </Link>
                                    ),
                                    queue: channel.queued_messages,
                                    channel_group_name: channel.channel_group_name,
                                    received: channel.received_messages,
                                    sent: channel.sent_messages,
                                    filtered: channel.filtered_messages,
                                    errors: channel.errored_messages,
                                    is_favorite: channel.is_favorite,
                                    support_tier:
                                      channel.support_tier ||
                                      supportTierBackup[channel.channel_id],
                                    suppress_support_event: channel.suppress_support_event,
                                    avg_process_time: channel.avg_process_time,
                                    mc_auth_type: authTypeMap[channel.mc_auth_type],
                                    lastReviewed: channel.last_reviewed_by_support
                                      ? new Date(
                                        channel.last_reviewed_by_support
                                      ).toLocaleString()
                                      : "Not Reviewed",
                                  };
                                })}
                                totalCount={card.totalCount}
                                functionCallBack={async (variables, isCSVDownload = false) => await fetchClientEnvironment(variables, isCSVDownload, card.id)}
                                setStateForPagnination={setStateForPagnination}
                                currentLimit={currentLimit}
                                currentOffset={currentOffset}
                                ascOrDescValue={ascOrDescValue}
                                sortValue={sortValue}
                                numberOfRowsData={numberOfRowsData}
                                columnMapping={columnMapping}
                                tableName="Channels"
                                CSVHeaders={headers}
                                CSVFileName={
                                  "Channels_" +
                                  clientEnvironmentData.client_environment_name +
                                  "_Export"
                                }
                                columns={[
                                  {
                                    Header: "Status",
                                    accessor: "status",
                                    filterable: false,
                                    minWidth: 120,
                                  },
                                  {
                                    Header: "Favorite",
                                    Cell: (cell) => {
                                      const row = cell.row.original;
                                      return (
                                        <i
                                          className={`fas fa-heart`}
                                          style={{
                                            cursor: 'pointer',
                                            color: row.is_favorite ? '#e74c3c' : '#bdc3c7',
                                            fontSize: '22px',
                                            transition: 'color 0.2s ease'
                                          }}
                                          onClick={() => toggleFavoriteChannel(row.id, card.id)}
                                          title={row.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                                        />
                                      );
                                    },
                                    accessor: "favorite",
                                    sortable: false,
                                    filterable: false,
                                    maxWidth: 80,
                                  },
                                  {
                                    Header: "Support Tier",
                                    accessor: (row) => {
                                      return mapSupportTier(row.support_tier);
                                    },
                                    filterable: false,
                                    hidden: !isInternalSpokeType,
                                    minWidth: 100,
                                  },
                                  {
                                    Header: "Group",
                                    Cell: (cell) => {
                                      const row = cell.row.original;
                                      return (
                                        <CustomTooltip
                                          id={`channel-group-id-${row.id}`}
                                          value={row.channel_group_name}
                                          placement="right"
                                        />
                                      );
                                    },
                                    accessor: "channel_group_name",
                                    filterable: false,
                                    minWidth: 150,
                                  },
                                  {
                                    Header: "Channel Name",
                                    Cell: (cell) => {
                                      const row = cell.row.original;
                                      return (
                                        <CustomTooltip
                                          id={`channel-id-${row.id}`}
                                          value={row.name}
                                          placement="right"
                                        />
                                      );
                                    },
                                    accessor: "name",
                                    filterable: false,
                                    minWidth: 200,
                                  },
                                  {
                                    Header: "View Messages",
                                    Cell: (cell) => {
                                      const row = cell.row.original;
                                      return (
                                        <CustomTooltip
                                          id={`view-messages-${row.id}`}
                                          value={row.action}
                                          placement="left"
                                        />
                                      );
                                    },
                                    accessor: "action",
                                    sortable: false,
                                    filterable: false,
                                    // hidden: !isInternalSpokeType,
                                    minWidth: 120,
                                  },
                                  {
                                    Header: "Blocked Email Alerts",
                                    accessor: (row) => {
                                      return row.suppress_support_event ? "Yes" : "No";
                                    },
                                    filterable: false,
                                    minWidth: 100,
                                  },
                                  {
                                    Header: "Received",
                                    accessor: (row) => {
                                      return row.received ? row.received : 0;
                                    },
                                    filterable: false,
                                    maxWidth: 100,
                                  },
                                  {
                                    Header: "Queued",
                                    accessor: (row) => {
                                      return row.queue ? row.queue : 0;
                                    },
                                    filterable: false,
                                    maxWidth: 100,
                                  },
                                  {
                                    Header: "Filtered",
                                    accessor: (row) => {
                                      return row.filtered ? row.filtered : 0;
                                    },
                                    filterable: false,
                                    maxWidth: 100,
                                  },
                                  {
                                    Header: "Errored",
                                    accessor: (row) => {
                                      return row.errors ? row.errors : 0;
                                    },
                                    filterable: false,
                                    maxWidth: 100,
                                  },
                                  {
                                    Header: "Sent/Transformed",
                                    accessor: (row) => {
                                      return row.sent ? row.sent : 0;
                                    },
                                    filterable: false,
                                    minWidth: 100,
                                  },
                                  {
                                    Header: "Avg Process Time (sec)",
                                    Cell: (cell) => {
                                      const row = cell.row.original;
                                      return (
                                        <CustomTooltip
                                          id={`avg-process-time-${row.channel_id}`}
                                          value={Math.round(10000 * row.avg_process_time) / 10000}
                                          placement="left"
                                        />
                                      );
                                    },
                                    accessor: "avg_process_time",
                                    filterable: false,
                                    minWidth: 120,
                                  },
                                  {
                                    Header: "Auth Type",
                                    Cell: (cell) => {
                                      const row = cell.row.original;
                                      return (
                                        <CustomTooltip
                                          id={`mc-auth-type-${row.channel_id}`}
                                          value={row.mc_auth_type}
                                          placement="left"
                                        />
                                      );
                                    },
                                    accessor: "mc_auth_type",
                                    filterable: false,
                                    hidden: !isInternalSpokeType,
                                    minWidth: 80,
                                  },
                                  {
                                    Header: "Last Reviewed",
                                    Cell: (cell) => {
                                      const row = cell.row.original;
                                      return (
                                        <CustomTooltip
                                          id={`last-reviewed-${row.channel_id}`}
                                          value={row.lastReviewed}
                                          placement="left"
                                        />
                                      );
                                    },
                                    accessor: "lastReviewed",
                                    filterable: false,
                                    hidden: !isInternalSpokeType,
                                    minWidth: 180,
                                  },
                                ]}
                                hidePagination={false}
                              />
                            </Col>
                          </Row>
                        )}
                      </Card.Body>
                    </div>
                  </Collapse>
                </Card>
              </Col>
            </Row>
          );
        })}
        <Modal
          toggle={() => setResourceDetailsModalOpen(!resourceDetailsModalOpen)}
          className="modal-large"
          isOpen={resourceDetailsModalOpen}
        >
          <div className="modal-header">
            <h5 className="modal-title" id="resourceDetailsModalLabel">
              Resource Details
            </h5>
            <button
              id="resource-details-close"
              aria-label="Close"
              className="close"
              type="button"
              onClick={() => setResourceDetailsModalOpen(false)}
            >
              <span aria-hidden={true}>x</span>
            </button>
          </div>
          <ModalBody>
            {!resourceDetails ? (
              "Not able to fetch any resource details."
            ) : (
              <div
                className="accordions"
                id="environmentResourceDetailsAccordion"
              >
                {resourceDetails.db_secrets?.length > 0 && (
                  <Card>
                    <Card.Header>
                      <Card.Title as="h4">
                        <a
                          data-toggle="collapse"
                          aria-expanded={multipleExpandablePanels.includes(1)}
                          href="#pablo"
                          onClick={(e) => toggleMultipleExpandablePanels(e, 1)}
                        >
                          Database Secrets <b className="caret"></b>
                        </a>
                      </Card.Title>
                    </Card.Header>
                    <Collapse
                      className="card-collapse"
                      id="dbSecretCollapse"
                      in={multipleExpandablePanels.includes(1)}
                    >
                      <Card.Body>
                        <Table className="table-hover table-striped w-full">
                          <tbody>
                            {resourceDetails.db_secrets?.map((db_secret) => {
                              return (
                                <tr key={`db-secret-${db_secret.key}`}>
                                  <td>{db_secret.key}</td>
                                  <td>{db_secret.value}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Collapse>
                  </Card>
                )}
                {resourceDetails.mc_secrets?.length > 0 && (
                  <Card>
                    <Card.Header>
                      <Card.Title as="h4">
                        <a
                          data-toggle="collapse"
                          aria-expanded={multipleExpandablePanels.includes(2)}
                          href="#pablo"
                          onClick={(e) => toggleMultipleExpandablePanels(e, 2)}
                        >
                          Mirth Connect Secrets <b className="caret"></b>
                        </a>
                      </Card.Title>
                    </Card.Header>
                    <Collapse
                      className="card-collapse"
                      id="mcSecretCollapse"
                      in={multipleExpandablePanels.includes(2)}
                    >
                      <Card.Body>
                        <Table className="table-hover table-striped w-full">
                          <tbody>
                            {resourceDetails.mc_secrets?.map((mc_secret) => {
                              return (
                                <tr key={`mc-secret-${mc_secret.key}`}>
                                  <td>{mc_secret.key}</td>
                                  <td>{mc_secret.value}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Collapse>
                  </Card>
                )}
                {resourceDetails.mc_nlb_dns_name && (
                  <Card>
                    <Card.Header>
                      <Card.Title as="h4">
                        <a
                          data-toggle="collapse"
                          id="mc-address"
                          aria-expanded={multipleExpandablePanels.includes(3)}
                          href="#pablo"
                          onClick={(e) => toggleMultipleExpandablePanels(e, 3)}
                        >
                          MC Address <b className="caret"></b>
                        </a>
                      </Card.Title>
                    </Card.Header>
                    <Collapse
                      className="card-collapse"
                      id="mcNLBDNSCollapse"
                      in={multipleExpandablePanels.includes(3)}
                    >
                      <Card.Body>
                        <Table className="table-hover table-striped w-full">
                          <tbody>
                            <tr key={`nlb-dns`}>
                              <td>MC Admin DNS</td>
                              <td id="mc-admin-dns">{`https://${resourceDetails.mc_nlb_dns_name}:8443/`}</td>
                              <td>
                                <Button
                                  variant="primary"
                                  className="btn-outline"
                                  onClick={() => {
                                    copyToClipboard(
                                      `https://${resourceDetails.mc_nlb_dns_name}:8443/`
                                    );
                                  }}
                                >
                                  <i className="fas fa-copy"></i>
                                </Button>
                              </td>
                            </tr>
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Collapse>
                  </Card>
                )}
                {resourceDetails.mc_authorized_data_sources?.length > 0 && (
                  <Card>
                    <Card.Header>
                      <Card.Title as="h4">
                        <a
                          data-toggle="collapse"
                          aria-expanded={multipleExpandablePanels.includes(4)}
                          href="#pablo"
                          onClick={(e) => toggleMultipleExpandablePanels(e, 4)}
                        >
                          Authorized Data Sources <b className="caret"></b>
                        </a>
                      </Card.Title>
                    </Card.Header>
                    <Collapse
                      className="card-collapse"
                      id="mcAuthorizedDataSourcesCollapse"
                      in={multipleExpandablePanels.includes(4)}
                    >
                      <Card.Body>
                        <Table className="table-hover table-striped w-full">
                          <tbody>
                            {resourceDetails.mc_authorized_data_sources?.map(
                              (mc_authorized_data_source, index) => {
                                return (
                                  <tr>
                                    <td>
                                      <Row>
                                        <Col md="12">
                                          API key value {index + 1}
                                        </Col>
                                      </Row>
                                    </td>
                                    <td>
                                      <Row>
                                        <Col md="4">User</Col>
                                        <Col md="8">
                                          {mc_authorized_data_source.user_name}
                                        </Col>
                                      </Row>
                                      <Row>
                                        <Col md="4">API Key</Col>
                                        <Col md="8">
                                          <span>
                                            {mc_authorized_data_source.api_key}
                                          </span>
                                        </Col>
                                      </Row>
                                      <Row>
                                        <Col md="4">Routes</Col>
                                        <Col md="8">
                                          {mc_authorized_data_source.routes.join(
                                            ", "
                                          )}
                                        </Col>
                                      </Row>
                                    </td>
                                  </tr>
                                );
                              }
                            )}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Collapse>
                  </Card>
                )}
              </div>
            )}
          </ModalBody>
        </Modal>
        <Modal
          toggle={() => setMCUserModalOpen(!mcUserModalOpen)}
          isOpen={mcUserModalOpen}
          scrollable={true}
        >
          <style type="text/css">
            {`
              .btn-small {
                padding: 0.1rem 0.1rem;
                font-size: 10px;
              }
            `}
          </style>
          <div className="modal-header">
            <h5 className="modal-title" id="mcUserModalLabel">
              Mirth Connect Administrator User Info
            </h5>
          </div>
          <ModalBody>
            <div>
              <b>Username:</b> {mcUserData.username}
              <Button
                variant="primary"
                className="btn-outline ml-2"
                size="small"
                onClick={() => {
                  copyToClipboard(mcUserData.username);
                }}
              >
                <i className="fas fa-copy"></i>
              </Button>
            </div>
            <div>
              <b>Password:</b> {mcUserData.mc_password}
              <Button
                variant="primary"
                className="btn-outline ml-2"
                size="small"
                onClick={() => {
                  copyToClipboard(mcUserData.mc_password);
                }}
              >
                <i className="fas fa-copy"></i>
              </Button>
            </div>
            <br></br>
            <div>
              <b>
                This MC User is valid until{" "}
                {new Date(mcUserData.ttl).toLocaleString("en-US")}
              </b>
            </div>
            <br></br>
            <div>
              <b>MC Address:</b>
            </div>
            <Table className="table-hover table-striped w-full">
              <tbody>
                <tr key={`nlb-dns`}>
                  <td>{`https://${mcUserData.mc_nlb_dns_name}:8443/`}</td>
                  <td>
                    <Button
                      variant="primary"
                      className="btn-outline ml-2"
                      size="small"
                      onClick={() => {
                        copyToClipboard(
                          `https://${mcUserData.mc_nlb_dns_name}:8443/`
                        );
                      }}
                    >
                      <i className="fas fa-copy"></i>
                    </Button>
                  </td>
                </tr>
              </tbody>
            </Table>
            <div>
              <b>Copy All:</b>
              <Button
                variant="primary"
                id="copy-all"
                className="btn-outline ml-2"
                size="small"
                onClick={() => {
                  copyToClipboard(
                    `Username:  ${mcUserData.username}\nPassword:  ${mcUserData.mc_password}\nNLB URL:   https://${mcUserData.mc_nlb_dns_name}:8443/`
                  );
                }}
              >
                <i className="fas fa-copy"></i>
              </Button>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              id="mc-user-modal-okay-button"
              variant="secondary"
              onClick={() => setMCUserModalOpen(false)}
            >
              OK
            </Button>
          </ModalFooter>
        </Modal>
        <Modal
          toggle={() => setChannelActionModalOpen(!channelActionModalOpen)}
          size="lg"
          isOpen={channelActionModalOpen}
          scrollable={false}
        >
          <div className="modal-header">
            <div className="modal-title" id="channelActionOptionsList">
              <h5>Select Channel Action Options</h5>
              {!isCodebuildAllowed && (
                <h5 className="text-danger">
                  *Note: No actions can be performed as this environment is
                  currently deploying.
                </h5>
              )}
            </div>
            <button
              aria-label="Close"
              className="close"
              type="button"
              onClick={() => setChannelActionModalOpen(false)}
            >
              <span aria-hidden={true}>x</span>
            </button>
          </div>
          <ModalBody>
            <Row>
              <Col>
                <DropdownButton
                  id="channel-action-all-dropdown"
                  variant="primary"
                  className="floatRight"
                  disabled={!isCodebuildAllowed}
                  onSelect={(key) =>
                    handleChannelActionAllSelect(
                      key,
                      clientEnvironmentData.channels.length
                    )
                  }
                  title="Apply to All "
                >
                  {actionOptionsList.map((item, index) => {
                    return (
                      <Dropdown.Item key={index} eventKey={item.key}>
                        {item.value}
                      </Dropdown.Item>
                    );
                  })}
                </DropdownButton>
              </Col>
              <Col className="text-right">
                {currentUser.groupName === "external" && (
                  <Button
                    variant="secondary"
                    className="btn-outline"
                    onClick={() => {
                      setSteps(channelActionModalSteps);
                      setCurrentStep(0);
                      setIsOpen(true);
                    }}
                  >
                    <i className="fas fa-desktop"></i> View Our Tour Guide
                  </Button>
                )}
              </Col>
            </Row>
            <Table>
              <thead>
                <tr>
                  <th>Channels</th>
                  <th id="select-action-to-take-column">
                    Select Action to Take
                  </th>
                </tr>
              </thead>
              <tbody>
                {clientEnvironmentData.channels?.map((channel, index) => {
                  return (
                    <tr key={index}>
                      <td>{channel.channel_name}</td>
                      <td>
                        <DropdownButton
                          id={`dropdown-basic-button-${index}`}
                          variant="primary"
                          className="floatRight"
                          disabled={!isCodebuildAllowed}
                          onSelect={(key, event) =>
                            handleMultiSelect(key, event, index)
                          }
                          title={
                            multiSelected[index]?.value ||
                            actionOptionsList[0].value
                          }
                        >
                          {isInternal &&
                          isInternalSpokeType &&
                          channel.channel_library_id
                            ? channelActionAllOptions.map((item, index) => {
                                return (
                                  <Dropdown.Item
                                    key={index}
                                    eventKey={item.key}
                                  >
                                    {item.value}
                                  </Dropdown.Item>
                                );
                              })
                            : channelActionBasicOptions.map((item, index) => {
                                return (
                                  <Dropdown.Item
                                    key={index}
                                    eventKey={item.key}
                                  >
                                    {item.value}
                                  </Dropdown.Item>
                                );
                              })}
                        </DropdownButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="secondary"
              onClick={() => setChannelActionModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!isCodebuildAllowed}
              onClick={() => {
                handleChannelActionSubmit(
                  clientId,
                  clientEnvironmentId,
                  clientEnvironmentData.channels,
                  currentUser.userId
                );
                setChannelActionModalOpen(false);
              }}
            >
              Submit
            </Button>
          </ModalFooter>
        </Modal>
        <Modal
          toggle={() => setModalOpen(!modalOpen)}
          isOpen={modalOpen}
          scrollable={true}
        >
          <div className="modal-header">
            <h5 className="modal-title" id="metricListModalLabel">
              Select CloudWatch Metric
            </h5>
            <button
              aria-label="Close"
              className="close"
              type="button"
              onClick={() => setModalOpen(false)}
            >
              <span aria-hidden={true}>x</span>
            </button>
          </div>
          <ModalBody>
            <ListGroup>{metricsListHTML}</ListGroup>
          </ModalBody>
          <ModalFooter>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setMetrics((oldMetrics) => [...oldMetrics, selectedMetric]);
                setModalOpen(false);
              }}
            >
              Show Metric
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </>
  );
}

export default Environment;
